import { Button, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import ScoutIcon from "components/ScoutIcon";
import { useMemo } from "react";

import { ScoutInput } from "./ScoutInput";
import {
  SCOUT_SUGGESTIONS,
  SCOUT_SUGGESTIONS_DISPLAY_COUNT,
  SCOUT_SUGGESTIONS_DISPLAY_COUNT_SMALL_SCREEN,
} from "./suggestions";

const SUGGESTIONS_PER_COLUMN = 2;

export function NewChat({
  onSubmit,
  onAbort,
  isSending,
  isAborting,
}: {
  onSubmit: (text: string) => Promise<void>;
  onAbort: () => void;
  isSending: boolean;
  isAborting: boolean;
}) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const suggestionCols = useMemo(() => {
    const picked = SCOUT_SUGGESTIONS.slice(
      0,
      isMediumScreen
        ? SCOUT_SUGGESTIONS_DISPLAY_COUNT_SMALL_SCREEN
        : SCOUT_SUGGESTIONS_DISPLAY_COUNT
    );
    const columns: (typeof picked)[] = [];
    for (let i = 0; i < picked.length; i += SUGGESTIONS_PER_COLUMN) {
      columns.push(picked.slice(i, i + SUGGESTIONS_PER_COLUMN));
    }
    return columns;
  }, [isMediumScreen]);

  return (
    <Stack
      sx={{
        flex: 1,
        width: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
        paddingTop: { xs: 8, sm: 9 },
        paddingBottom: { xs: 10, sm: 12 },
        transform: "translateY(calc(-2vh - 100px))",
        gap: 5,
      }}
    >
      <ScoutIcon sx={{ color: theme.palette.encampBlue.main, width: 80, height: 80 }} />
      <Typography variant="h6" textAlign="center">
        Hey there! I'm Scout, your Encamp guide. How can I help you today?
      </Typography>

      {isMediumScreen && suggestionCols.length > 0 && (
        <Stack sx={{ maxWidth: "920px" }}>
          <Typography variant="caption" color="text.secondary" paddingLeft={4}>
            Suggestions
          </Typography>
          <Stack direction="row" justifyContent="center" flexWrap="wrap">
            {suggestionCols
              .flatMap((row) => row)
              .map((suggestion) => (
                <Button
                  key={suggestion.label}
                  variant="contained"
                  color="encampBlue"
                  disabled={isSending}
                  onClick={() => onSubmit(suggestion.prompt)}
                  sx={{ textTransform: "none", m: 1 }}
                >
                  {suggestion.label}
                </Button>
              ))}
          </Stack>
        </Stack>
      )}

      {!isMediumScreen && suggestionCols.length > 0 && (
        <Stack spacing={3} sx={{ maxWidth: "920px" }}>
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Suggestions
            </Typography>
            <Stack spacing={3} direction="row" flexWrap="wrap">
              <Stack direction="column" spacing={3} alignItems="flex-end" flexWrap="wrap">
                {suggestionCols[0]?.map((suggestion) => (
                  <Button
                    key={suggestion.label}
                    variant="contained"
                    color="encampBlue"
                    disabled={isSending}
                    onClick={() => onSubmit(suggestion.prompt)}
                    sx={{ textTransform: "none" }}
                  >
                    {suggestion.label}
                  </Button>
                ))}
              </Stack>

              {suggestionCols.slice(1)[0]?.length > 0 && (
                <Stack direction="column" spacing={3} alignItems="flex-start" flexWrap="wrap">
                  {suggestionCols.slice(1)[0]?.map((suggestion) => (
                    <Button
                      key={suggestion.label}
                      variant="contained"
                      color="encampBlue"
                      disabled={isSending}
                      onClick={() => onSubmit(suggestion.prompt)}
                      sx={{ textTransform: "none" }}
                    >
                      {suggestion.label}
                    </Button>
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
      )}

      <ScoutInput
        onSubmit={onSubmit}
        onAbort={onAbort}
        isSending={isSending}
        isAborting={isAborting}
        placeholder="Check regulations, help me understand compliance requirements..."
      />
    </Stack>
  );
}
